// src/AdminPanel/ContentManagement.js
import React, { useState, useEffect } from "react";
import { useContentInfo, DEFAULT_CONTENT_INFO } from "../contexts/ContentContext";
import { useSale } from "../contexts/SaleContext";
import toast from "../utils/toast";
import CloudinaryUpload from "../components/CloudinaryUpload";

const SLIDE_LABELS = ["Hero Section Slide 1", "Hero Section Slide 2", "Hero Section Slide 3"];



function ContentManagement() {
  const { contentInfo, loading, saveContentInfo } = useContentInfo();
  const { sale, discountLabel } = useSale();

  const [logoURL, setLogoURL] = useState(DEFAULT_CONTENT_INFO.logoURL);
  const [footerLogoURL, setFooterLogoURL] = useState(DEFAULT_CONTENT_INFO.footerLogoURL);
  const [slides, setSlides] = useState(DEFAULT_CONTENT_INFO.slides);
  const [contact, setContact] = useState(DEFAULT_CONTENT_INFO.contact);
  const [bankInfo, setBankInfo] = useState(DEFAULT_CONTENT_INFO.bankInfo);
  const [ads, setAds] = useState(DEFAULT_CONTENT_INFO.ads);
  const [saving, setSaving] = useState(false);

  const isFormValid =
    (logoURL?.trim() || "") !== "" &&
    (footerLogoURL?.trim() || "") !== "" &&
    (contact.phoneNumber?.trim() || "") !== "" &&
    slides.every(s =>
      (s.heroTitle?.trim() || "") !== "" &&
      (s.heroImageURL?.trim() || "") !== ""
    );

  // Sync local state only once when Firestore data first loads
  useEffect(() => {
    if (!loading) {
      setLogoURL(contentInfo.logoURL);
      setFooterLogoURL(contentInfo.footerLogoURL);
      setSlides(contentInfo.slides);
      setContact(contentInfo.contact);
      setBankInfo(contentInfo.bankInfo);
      setAds(contentInfo.ads || DEFAULT_CONTENT_INFO.ads);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);



  const updateSlide = (index, field, value) => {
    setSlides((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const slidesToSave = sale.isActive && discountLabel
        ? slides.map((s) => ({ ...s, heroCTAButtonText: discountLabel }))
        : slides;
      await saveContentInfo({ logoURL, footerLogoURL, slides: slidesToSave, contact, bankInfo, ads });
      toast.success("Content info saved successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save content info");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="upload-page">
        <div className="upload-container">
          <p style={{ textAlign: "center", padding: "40px" }}>
            <i className="fa-solid fa-spinner fa-spin"></i> Loading...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="upload-page">
      <div className="upload-container">
        <h2>Content Management</h2>
        <p style={{ color: "#888", marginBottom: "24px", fontSize: "14px" }}>
          Changes are saved to the database and reflected on the website immediately.
        </p>

        {/* ── App Logo ── */}
        <h3 style={{ marginBottom: "16px", color: "#d2222d", borderBottom: "2px solid #d2222d", paddingBottom: "6px" }}>
          <i className="fa-solid fa-star" style={{ marginRight: "8px" }}></i>
          App Logo
        </h3>

        <div
          style={{
            border: "1px solid #e0e0e0",
            borderRadius: "10px",
            padding: "20px",
            marginBottom: "28px",
            background: "#fafafa",
          }}
        >
          <div className="upload-form" style={{ display: "grid", gap: "12px" }}>
            <CloudinaryUpload
              id="cm-logo"
              label="Logo Image"
              required
              value={logoURL || ""}
              onChange={setLogoURL}
              previewShape="wide"
              previewStyle={{ height: "40px", maxWidth: "150px", objectFit: "contain" }}
            />
          </div>
        </div>

        {/* ── Footer Logo ── */}
        <h3 style={{ marginBottom: "16px", color: "#d2222d", borderBottom: "2px solid #d2222d", paddingBottom: "6px" }}>
          <i className="fa-solid fa-star-half-stroke" style={{ marginRight: "8px" }}></i>
          Footer Logo
        </h3>

        <div
          style={{
            border: "1px solid #e0e0e0",
            borderRadius: "10px",
            padding: "20px",
            marginBottom: "28px",
            background: "#fafafa",
          }}
        >
          <div className="upload-form" style={{ display: "grid", gap: "12px" }}>
            <CloudinaryUpload
              id="cm-footer-logo"
              label="Footer Logo Image"
              required
              value={footerLogoURL || ""}
              onChange={setFooterLogoURL}
              previewShape="wide"
              previewStyle={{ height: "40px", maxWidth: "150px", objectFit: "contain" }}
            />
          </div>
        </div>

        {/* ── Hero Slides ── */}
        <h3 style={{ marginBottom: "16px", color: "#d2222d", borderBottom: "2px solid #d2222d", paddingBottom: "6px" }}>
          <i className="fa-solid fa-image" style={{ marginRight: "8px" }}></i>
          Hero Section Slides
        </h3>

        {slides.map((slide, index) => (
          <div
            key={index}
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: "10px",
              padding: "20px",
              marginBottom: "20px",
              background: "#fafafa",
            }}
          >
            <h4 style={{ marginBottom: "14px", color: "#333" }}>
              <i className="fa-solid fa-layer-group" style={{ marginRight: "6px", color: "#d2222d" }}></i>
              {SLIDE_LABELS[index]}
            </h4>

            <div className="upload-form" style={{ display: "grid", gap: "12px" }}>
              <div className="form-group">
                <label>Hero Section Tagline (small top text above heading)</label>
                <input
                  type="text"
                  value={slide.heroTagline}
                  onChange={(e) => updateSlide(index, "heroTagline", e.target.value)}
                  placeholder="e.g. HealthCare sa mily Gi"
                />
              </div>

              <div className="form-group">
                <label>Hero Section Title (main heading)</label>
                <input
                  type="text"
                  value={slide.heroTitle}
                  onChange={(e) => updateSlide(index, "heroTitle", e.target.value)}
                  placeholder="e.g. 100% Genuine Dwaai"
                />
              </div>

              <div className="form-group">
                <label>Hero Section Title Highlight (large bold span)</label>
                <input
                  type="text"
                  value={slide.heroTitleHighlight}
                  onChange={(e) => updateSlide(index, "heroTitleHighlight", e.target.value)}
                  placeholder="e.g. Ab Gar Bethey!"
                />
              </div>

              <div className="form-group">
                <label>Hero Section Subtitle (description below heading)</label>
                <input
                  type="text"
                  value={slide.heroSubtitle}
                  onChange={(e) => updateSlide(index, "heroSubtitle", e.target.value)}
                  placeholder="e.g. Order Your Medicines Now"
                />
              </div>

              <div className="form-group">
                <label>
                  Hero Section CTA Button Text
                  {sale.isActive && (
                    <span style={{ marginLeft: "8px", fontSize: "12px", color: "#d2222d", fontWeight: "600" }}>
                      (Auto-filled from active sale — {discountLabel})
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  value={sale.isActive ? discountLabel : slide.heroCTAButtonText}
                  onChange={(e) => updateSlide(index, "heroCTAButtonText", e.target.value)}
                  placeholder="e.g. Upto 10% OFF"
                  disabled={sale.isActive}
                />
                {sale.isActive && (
                  <small style={{ color: "#d2222d", marginTop: "4px", display: "block" }}>
                    Button will show: <strong>{discountLabel}</strong> — managed via Manage Sale page.
                  </small>
                )}
              </div>

              <CloudinaryUpload
                id={`cm-slide-img-${index}`}
                label="Hero Section Slide Image"
                required
                value={slide.heroImageURL || ""}
                onChange={(url) => updateSlide(index, "heroImageURL", url)}
                previewShape="wide"
              />
            </div>
          </div>
        ))}

        {/* ── Ads / Gallery ── */}
        <h3 style={{ marginBottom: "16px", color: "#d2222d", borderBottom: "2px solid #d2222d", paddingBottom: "6px" }}>
          <i className="fa-solid fa-rectangle-ad" style={{ marginRight: "8px" }}></i>
          Ad 1 & Ad 2
        </h3>
        
        <div style={{ border: "1px solid #e0e0e0", borderRadius: "10px", padding: "20px", marginBottom: "28px", background: "#fafafa" }}>
          <div className="upload-form" style={{ display: "grid", gap: "20px" }}>
            <CloudinaryUpload
              id="cm-ad1"
              label="Ad 1 Image"
              value={(ads.ad1URL || "").replace(/^\.\//, "/")}
              onChange={(url) => setAds(prev => ({ ...prev, ad1URL: url }))}
              previewShape="wide"
            />
            <CloudinaryUpload
              id="cm-ad2"
              label="Ad 2 Image"
              value={(ads.ad2URL || "").replace(/^\.\//, "/")}
              onChange={(url) => setAds(prev => ({ ...prev, ad2URL: url }))}
              previewShape="wide"
            />
          </div>
        </div>

        {/* ── Current Sale Info ── */}
        <h3 style={{ marginBottom: "16px", color: "#d2222d", borderBottom: "2px solid #d2222d", paddingBottom: "6px" }}>
          <i className="fa-solid fa-percent" style={{ marginRight: "8px" }}></i>
          Current Sale
        </h3>

        <div style={{
          border: "1px solid #e0e0e0",
          borderRadius: "10px",
          padding: "20px",
          marginBottom: "28px",
          background: sale.isActive ? "#fff5f5" : "#fafafa",
        }}>
          {sale.isActive ? (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                <span style={{ background: "#d2222d", color: "#fff", borderRadius: "6px", padding: "4px 12px", fontWeight: "700", fontSize: "15px" }}>
                  {discountLabel}
                </span>
                <span style={{ background: "#e8f5e9", color: "#2e7d32", borderRadius: "6px", padding: "4px 10px", fontWeight: "600", fontSize: "13px" }}>
                  Active
                </span>
              </div>
              {sale.label && <p style={{ margin: "4px 0", fontSize: "14px", color: "#333" }}><strong>Sale:</strong> {sale.label}</p>}
              {sale.startDate && sale.endDate && (
                <p style={{ margin: "4px 0", fontSize: "14px", color: "#555" }}>
                  <strong>Duration:</strong> {sale.startDate} → {sale.endDate}
                </p>
              )}
              <p style={{ margin: "10px 0 0", fontSize: "13px", color: "#888" }}>
                Manage this sale from the <strong>Manage Sale</strong> page in the sidebar.
              </p>
            </>
          ) : (
            <p style={{ color: "#aaa", fontSize: "14px", margin: 0 }}>
              <i className="fa-solid fa-circle-info" style={{ marginRight: "6px" }}></i>
              No active sale. Go to <strong>Manage Sale</strong> to configure one.
            </p>
          )}
        </div>

        {/* ── Contact Info ── */}
        <h3 style={{ margin: "28px 0 16px", color: "#d2222d", borderBottom: "2px solid #d2222d", paddingBottom: "6px" }}>
          <i className="fa-solid fa-phone" style={{ marginRight: "8px" }}></i>
          Contact Information
        </h3>

        <div
          style={{
            border: "1px solid #e0e0e0",
            borderRadius: "10px",
            padding: "20px",
            background: "#fafafa",
          }}
        >
          <div className="upload-form" style={{ display: "grid", gap: "12px" }}>
            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="text"
                value={contact.phoneNumber}
                onChange={(e) => setContact((prev) => ({ ...prev, phoneNumber: e.target.value }))}
                placeholder="e.g. +06323839204"
              />
            </div>

            <div className="form-group">
              <label>WhatsApp Phone Number</label>
              <input
                type="text"
                value={contact.whatsappNumber}
                onChange={(e) => setContact((prev) => ({ ...prev, whatsappNumber: e.target.value }))}
                placeholder="e.g. +06378010848"
              />
            </div>

            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={contact.email || ""}
                onChange={(e) => setContact((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="e.g. info@dawaacare.com"
              />
            </div>

            <div className="form-group">
              <label>Location / Address</label>
              <input
                type="text"
                value={contact.location || ""}
                onChange={(e) => setContact((prev) => ({ ...prev, location: e.target.value }))}
                placeholder="e.g. Opposite Ikram Hospital, Bhimber road Gujrat"
              />
            </div>
          </div>
        </div>

        {/* ── Bank Deposit Info ── */}
        <h3 style={{ margin: "28px 0 16px", color: "#d2222d", borderBottom: "2px solid #d2222d", paddingBottom: "6px" }}>
          <i className="fa-solid fa-building-columns" style={{ marginRight: "8px" }}></i>
          Bank Deposit Information
        </h3>
        <p style={{ color: "#888", marginBottom: "16px", fontSize: "13px" }}>
          These details are shown to customers who choose "Bank Deposit" at checkout.
        </p>

        <div style={{ border: "1px solid #e0e0e0", borderRadius: "10px", padding: "20px", marginBottom: "28px", background: "#fafafa" }}>
          <div className="upload-form" style={{ display: "grid", gap: "12px" }}>
            <div className="form-group">
              <label>Bank Name</label>
              <input
                type="text"
                value={bankInfo.bankName}
                onChange={(e) => setBankInfo((prev) => ({ ...prev, bankName: e.target.value }))}
                placeholder="e.g. SADAPAY - Visa"
              />
            </div>
            <div className="form-group">
              <label>Account Holder Name</label>
              <input
                type="text"
                value={bankInfo.accountHolder}
                onChange={(e) => setBankInfo((prev) => ({ ...prev, accountHolder: e.target.value }))}
                placeholder="e.g. IQBAL HASSAN"
              />
            </div>
            <div className="form-group">
              <label>Account Number / IBAN</label>
              <input
                type="text"
                value={bankInfo.accountNumber}
                onChange={(e) => setBankInfo((prev) => ({ ...prev, accountNumber: e.target.value }))}
                placeholder="e.g. 03107949622"
              />
            </div>
            <div className="form-group">
              <label>WhatsApp Number (for screenshot)</label>
              <input
                type="text"
                value={bankInfo.whatsappNumber}
                onChange={(e) => setBankInfo((prev) => ({ ...prev, whatsappNumber: e.target.value }))}
                placeholder="e.g. 03121798713"
              />
            </div>
          </div>
        </div>



        {/* ── Save Button ── */}
        <form className="upload-form" style={{ marginTop: "28px" }} onSubmit={handleSave}>
          <button type="submit" disabled={saving || !isFormValid}>
            {saving
              ? <><i className="fa-solid fa-spinner fa-spin"></i> Saving...</>
              : <><i className="fa-solid fa-floppy-disk"></i> Save Changes</>
            }
          </button>
        </form>
      </div>
    </div>
  );
}

export default ContentManagement;
